import { useMemo } from 'react';

import { useProperties } from 'framework-listing-lib/internal/providers/PropertiesProvider';
import { useListingLibData } from 'framework-listing-lib/internal/providers/DataProvider';
import useObjectTypeId from './useObjectTypeId';

export function useCurrentTypeDefinition() {
  'use memo';
  const currentObjectTypeId = useObjectTypeId();
  const { getTypeDefinition } = useListingLibData();
  const { typeDefinitionOverride } = useProperties();

  return useMemo(() => {
    if (typeDefinitionOverride) {
      return typeDefinitionOverride;
    }

    // Memoized at the provider level
    return getTypeDefinition(currentObjectTypeId);
  }, [currentObjectTypeId, getTypeDefinition, typeDefinitionOverride]);
}

export function useTypeDefinition(
  {
    objectTypeId,
  }: {
    /** The objectTypeId of the typeDefinition to return, defaults to the current objectTypeId of the table */
    objectTypeId?: string;
  } = { objectTypeId: undefined }
) {
  'use memo';
  const currentTypeDefinition = useCurrentTypeDefinition();
  const { getTypeDefinition } = useListingLibData();
  const currentObjectTypeId = useObjectTypeId();

  return useMemo(() => {
    if (!objectTypeId || objectTypeId === currentObjectTypeId) {
      return currentTypeDefinition;
    }
    return getTypeDefinition(objectTypeId);
  }, [
    currentObjectTypeId,
    currentTypeDefinition,
    getTypeDefinition,
    objectTypeId,
  ]);
}

export function useCurrentTypeDefinitionName() {
  'use memo';
  const currentTypeDefinition = useCurrentTypeDefinition();

  return currentTypeDefinition
    ? currentTypeDefinition.fullyQualifiedName
    : undefined;
}
